// @spec SHL-STR-017
// @spec SHL-STR-018
// TanStack Query hook for platform-wide search with debounce and filters

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { searchPlatform, type DateRangeFilter, type SearchCategory } from '@/api/search';
import { useDebounce } from '@/hooks/use-debounce';
import { useRecentSearches } from '@/hooks/use-recent-searches';
import { createApiClient } from '@/lib/api-client';

/**
 * Debounce delay in milliseconds for search queries.
 * Balances responsiveness with server load.
 */
const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Minimum query length required to trigger search.
 */
const MIN_QUERY_LENGTH = 2;

/** Category info for filter display */
export interface CategoryInfo {
  name: string;
  displayName: string;
}

/**
 * Return type for the useSearch hook.
 */
export interface UseSearchReturn {
  /** Current search query */
  query: string;
  /** Set search query */
  setQuery: (query: string) => void;
  /** Debounced query (300ms) */
  debouncedQuery: string;
  /** Search results grouped by category */
  results: SearchCategory[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Whether search has been performed (query >= 2 chars) */
  hasSearched: boolean;
  /** Execute search and add to recent searches */
  executeSearch: () => void;
  /** Currently selected category filters */
  selectedCategories: string[];
  /** Toggle a category filter on/off */
  toggleCategory: (category: string) => void;
  /** Current date range filter */
  dateRange: DateRangeFilter | null;
  /** Set date range filter */
  setDateRange: (range: DateRangeFilter | null) => void;
  /** Clear all filters */
  clearFilters: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Available categories from API response */
  availableCategories: CategoryInfo[];
}

/**
 * Query key factory for search queries with filters.
 */
export const searchQueryKey = (
  query: string,
  categories: string[],
  dateRange: DateRangeFilter | null
) => ['search', query, categories, dateRange] as const;

/**
 * Map of category names to display names.
 */
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  members: 'Team Members',
  organizations: 'Organizations',
};

/**
 * Hook for platform-wide search with debounce and recent searches integration.
 *
 * Features:
 * - 300ms debounce before triggering API call
 * - Minimum 2 character query length
 * - Integration with recent searches
 * - TanStack Query for caching and state management
 *
 * @example
 * function GlobalSearch() {
 *   const {
 *     query,
 *     setQuery,
 *     results,
 *     isLoading,
 *     hasSearched,
 *     executeSearch
 *   } = useSearch();
 *
 *   return (
 *     <Command.Dialog>
 *       <SearchInput
 *         value={query}
 *         onValueChange={setQuery}
 *         onSubmit={executeSearch}
 *       />
 *       {hasSearched && (
 *         <SearchResults
 *           results={results}
 *           isLoading={isLoading}
 *           query={query}
 *         />
 *       )}
 *     </Command.Dialog>
 *   );
 * }
 */
export function useSearch(): UseSearchReturn {
  const { getToken } = useAuth();
  const { addSearch } = useRecentSearches();
  const [query, setQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeFilter | null>(null);

  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_DELAY);
  const shouldSearch = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const searchQuery = useQuery({
    queryKey: searchQueryKey(debouncedQuery, selectedCategories, dateRange),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      // Build params object, only including optional fields if they have values
      const params: Parameters<typeof searchPlatform>[1] = {
        q: debouncedQuery,
        limit: 5,
      };
      if (selectedCategories.length > 0) {
        params.categories = selectedCategories;
      }
      if (dateRange !== null) {
        params.dateRange = dateRange;
      }
      return searchPlatform(apiClient, params);
    },
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds
  });

  const executeSearch = useCallback(() => {
    const trimmed = query.trim();
    if (trimmed.length >= MIN_QUERY_LENGTH) {
      addSearch(trimmed);
    }
  }, [query, addSearch]);

  const toggleCategory = useCallback((category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedCategories([]);
    setDateRange(null);
  }, []);

  const hasActiveFilters = selectedCategories.length > 0 || dateRange !== null;

  // Extract available categories from API response or use defaults
  const availableCategories: CategoryInfo[] = searchQuery.data?.availableFilters.categories.map(
    (name) => ({
      name,
      displayName: CATEGORY_DISPLAY_NAMES[name] ?? name,
    })
  ) ?? [
    { name: 'members', displayName: 'Team Members' },
    { name: 'organizations', displayName: 'Organizations' },
  ];

  return {
    query,
    setQuery,
    debouncedQuery,
    results: searchQuery.data?.categories ?? [],
    isLoading: searchQuery.isLoading && shouldSearch,
    error: searchQuery.error,
    hasSearched: shouldSearch,
    executeSearch,
    selectedCategories,
    toggleCategory,
    dateRange,
    setDateRange,
    clearFilters,
    hasActiveFilters,
    availableCategories,
  };
}
